import {
  createStyles,
  InputAdornment,
  makeStyles,
  TextField,
} from "@material-ui/core";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { ResourceCustomTheme } from "styles/config";

type StyleProps = {
  colorHex: string;
  slim?: boolean;
};
const useStyles = makeStyles<ResourceCustomTheme, StyleProps>(
  (_theme: ResourceCustomTheme) =>
    createStyles({
      brandColorInput: {
        display: "flex",
        width: "100%",
      },
      colorPickerWrap: {
        position: "relative",
        width: ({ slim }) => (slim ? "2.5rem" : "3.5rem"),
        height: ({ slim }) => (slim ? "2.5rem" : "3.5rem"),
      },
      primaryColorPreview: {
        content: '""',
        width: ({ slim }) => (slim ? "2.5rem" : "3.5rem"),
        height: ({ slim }) => (slim ? "2.5rem" : "3.5rem"),
        borderTopLeftRadius: "0.2rem",
        borderBottomLeftRadius: "0.2rem",
        backgroundColor: ({ colorHex }) => `#${colorHex}`,
        position: "absolute",
        top: 0,
        left: 0,
      },
      primaryColorInput: {
        opacity: 0,
        position: "absolute",
        left: 0,
        top: 0,
        cursor: "pointer",
      },
      primaryColorTextFieldContainer: {
        flexShrink: 100,
      },
      primaryColorTextField: {
        borderRadius: "0 0.25rem 0.25rem 0",
      },
    })
);

type ColorPickerProps = {
  colorHex: string;
  setColorHex(colorHex: string): void;
  slim?: boolean;
  disabled?: boolean;
};

const ColorPicker: React.FC<ColorPickerProps> = ({
  colorHex,
  setColorHex,
  slim,
  disabled,
}) => {
  const [textValue, setTextValue] = useState(colorHex);

  useEffect(() => {
    setTextValue(colorHex);
  }, [colorHex]);

  const classes = useStyles({
    colorHex,
    slim,
  });

  const pickerRef = useRef<HTMLInputElement>(null);

  const debouncedSetColorHex = _.debounce(setColorHex, 200);
  const debouncedSetTextValue = _.debounce(setTextValue, 200);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const cleanedValue = e.target.value.trim();
    debouncedSetColorHex(cleanedValue);
    debouncedSetTextValue(cleanedValue);
  };

  const handleSelectColor = () => {
    if (disabled) return;
    pickerRef.current?.click();
  };

  return (
    <div className={classes.brandColorInput}>
      <div className={classes.colorPickerWrap}>
        <div
          role="button"
          tabIndex={0}
          onKeyUp={handleSelectColor}
          aria-label="color picker"
          className={classes.primaryColorPreview}
          onClick={handleSelectColor}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        />
        <input
          value={`#${colorHex}`}
          className={classes.primaryColorInput}
          disabled={disabled}
          width={0}
          ref={pickerRef}
          type="color"
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          onChange={(e) => {
            debouncedSetColorHex(e.target.value.slice(1));
          }}
        />
      </div>
      <TextField
        className={classes.primaryColorTextFieldContainer}
        disabled={disabled}
        InputProps={{
          className: classes.primaryColorTextField,
          startAdornment: (
            <InputAdornment style={{ marginRight: 0 }} position="start">
              #
            </InputAdornment>
          ),
          margin: slim ? "dense" : "none",
        }}
        /* eslint-disable-next-line react/jsx-no-duplicate-props */
        inputProps={{
          maxLength: 6,
        }}
        variant="outlined"
        value={textValue}
        onChange={handleColorChange}
      />
    </div>
  );
};

export default ColorPicker;
